<template>
    <slot name="activator" :props="activatorSlotParams" />
    <v-overlay
        v-model="_dialog"
        :width="mobile ? '100%' : undefined"
        :class="{ 'align-center': !mobile, 'align-end': mobile, 'justify-center': true }"
        @after-enter="afterTransition"
        :eager="eager"
    >
        <transition :name="transition">
            <div
                data-cy="oss-dialog-container"
                class="d-flex space-x-2 ossDialogContainer"
                v-if="!mobile"
                :style="{
                    width: width,
                    height: height,
                    maxWidth: maxWidth,
                    minWidth: minWidth,
                    padding: '4px',
                }"
            >
                <div
                    data-cy="oss-inner-dialog-container"
                    :style="{
                        paddingLeft: '56px',
                        width: '100%',
                        minHeight: minHeight,
                        maxHeight: maxHeight,
                        background: bgColor,
                    }"
                >
                    <v-card rounded="xl" class="mb-2">
                        <template #title v-if="!withoutHeader">
                            <v-row justify="space-between">
                                <v-col cols="1">
                                    <v-btn-ternary
                                        v-if="hasPreviousButton"
                                        @click.stop="previousEvent()"
                                        :disabled="previousDisabled"
                                        icon="$chevron-left"
                                        :data-cy="prefixDataCy + '_previous'"
                                    ></v-btn-ternary>
                                </v-col>
                                <v-col cols="10" class="text-center">
                                    {{ dialogTitle }}
                                </v-col>
                                <v-col cols="1">
                                    <slot name="after">
                                        <v-btn
                                            v-if="hasAfterButton"
                                            @click.stop="afterEvent()"
                                            :disabled="afterDisabled"
                                            icon="$chevron_right"
                                        ></v-btn>
                                    </slot>
                                </v-col>
                            </v-row>
                        </template>
                        <template v-slot:text>
                            <div
                                class="overflow-y-auto"
                                :style="{
                                    maxHeight: hasValidButton ? '85vh' : '95vh',
                                }"
                            >
                                <slot />
                            </div>
                        </template>
                    </v-card>
                    <slot name="actions" :props="actionSlotParams">
                        <v-btn-primary
                            v-if="hasValidButton"
                            block
                            size="x-large"
                            @click="validEvent"
                            :disabled="dialogDisabled"
                            :loading="loading || validLoading"
                            :data-cy="prefixDataCy + '_action'"
                            :class="{
                                error: error,
                            }"
                        >
                            {{ dialogActionTitle }}
                        </v-btn-primary>
                    </slot>
                </div>

                <!-- Close/Reduce CTA -->
                <v-hover>
                    <template #default="{ isHovering, props }">
                        <div class="rounded-pill oss-dialog-close ml-2" v-bind="props">
                            <v-btn
                                icon="$oss-close"
                                @click="closeDialog()"
                                color="white"
                                variant="flat"
                                size="large"
                                rounded="pill"
                                :data-cy="prefixDataCy + '_close'"
                            ></v-btn>
                            <v-scroll-y-transition>
                                <v-btn
                                    v-if="isHovering"
                                    icon="$fa-minus"
                                    @click="reduceDialog()"
                                    color="white"
                                    variant="flat"
                                    size="large"
                                    rounded="pill"
                                    :data-cy="prefixDataCy + '_minus'"
                                ></v-btn>
                            </v-scroll-y-transition>
                        </div>
                    </template>
                </v-hover>
            </div>
            <div v-else>
                <v-card
                    :min-height="minHeight"
                    :height="height"
                    class="pa-2"
                    :style="{
                        width: '100%',
                    }"
                >
                    <v-row
                        no-gutters
                        v-touch="{
                            down: () => (_dialog = false),
                        }"
                    >
                        <v-col cols="4"> </v-col>
                        <v-col cols="4" class="d-flex align-center">
                            <v-divider></v-divider>
                        </v-col>
                        <v-col cols="4" class="d-flex justify-end align-center pa-2">
                            <v-btn-icon
                                elevation="0"
                                size="small"
                                icon="$oss-close"
                                @click="closeDialog"
                            />
                        </v-col>
                    </v-row>
                    <div class="d-flex justify-center mb-4 text-h6">
                        {{ dialogTitle }}
                    </div>
                    <div class="d-flex justify-center">
                        <slot />
                    </div>
                    <slot name="actions" :props="actionSlotParams">
                        <v-btn-primary
                            v-if="hasValidButton"
                            block
                            size="x-large"
                            @click="validEvent"
                            :disabled="dialogDisabled"
                            :loading="loading || validLoading"
                            :data-cy="prefixDataCy + '_action'"
                            :class="{
                                error: error,
                            }"
                        >
                            {{ dialogActionTitle }}
                        </v-btn-primary>
                    </slot>
                </v-card>
            </div>
        </transition>
    </v-overlay>
    <v-snackbar v-model="snackbarDialog" :timeout="-1" color="white">
        <span class="text-secondary">{{ dialogTitle }}</span>
        <template #actions>
            <div class="d-flex justify-space-between">
                <v-btn-icon size="24" icon="$add" color="secondary" @click="mountDialog()">
                </v-btn-icon>
                <v-btn-icon
                    size="24"
                    icon="$close"
                    color="secondary"
                    @click="closeDialog()"
                ></v-btn-icon>
            </div>
        </template>
    </v-snackbar>
</template>

<script setup lang="ts">
import { getCurrentInstance, computed, ref } from "vue";
import { DialogAction } from "@/services/dialogAction";
import { useDisplay } from "vuetify";

const emits = defineEmits<{
    close: [Boolean];
    valid: [];
    previous: [];
    after: [];
    afterEnter: [];
}>();

const currentInstance = getCurrentInstance();

interface DialogProps {
    /** The main title of the dialog */
    title?: string;
    /** The title displayed for the validation step */
    validTitle?: string;
    /** should the dialog be sized fullscreen?  */
    fullscreen?: boolean;
    /** class to apply to validation elements */
    validClass?: string;

    /** is the overlay to be hidden? */
    hideOverlay?: boolean;

    width?: string;
    height?: string;
    maxWidth?: string;
    minWidth?: string;
    minHeight?: string;
    maxHeight?: string;

    /** is validation disabled? */
    validDisabled?: boolean;
    /** validation buttons text */
    validText?: string;

    /** Indicates if the dialog is in a loading state */
    loading?: boolean;
    /** Actions available in the dialog */
    actions?: { action: DialogAction };

    /** Disables the 'previous' button if true */
    previousDisabled?: boolean;
    /** Disables the 'after' button if true */
    afterDisabled?: boolean;

    /** Shows the 'previous' button if true */
    previousDisplay?: boolean;
    /** Shows the 'next' button if true */
    nextDisplay?: boolean;

    /** Hides the validation step if true */
    hideValid?: boolean;

    withoutHeader?: boolean;

    validLoading?: boolean;

    prefixDataCy?: string;

    persistent?: boolean;

    error?: boolean; // TO USE => ROMAIN
    /** the return value that this ossDialog provides */
    returnValue?: any;
    transition?: {
        type: string;
        default: "dialog-transition"; // Fallback transition
    };
    bgColor?: string;

    eager?: boolean;
}

const { mobile } = useDisplay();

const props = defineProps<DialogProps>();

const dialog = defineModel<boolean>({
    default: false,
});

const snackbarDialog = ref<boolean>(false);

const _dialog = computed({
    get: () => dialog.value && !snackbarDialog.value,
    set: (value: boolean) => {
        dialog.value = value;
        snackbarDialog.value = false;
    },
});

const localLoading = ref<boolean>(false);

const $props = computed<Record<string, unknown>>(() => currentInstance?.vnode?.props || {});

const shouldEmitValidation = computed<boolean>(() => !!$props.value.onValid);
const shouldEmitPrevious = computed<boolean>(() => !!$props.value.onPrevious);
const shouldEmitAfter = computed(() => props.afterDisabled);

const hasFunctionInActions = computed(
    () =>
        !!props.actions?.action?.function &&
        props.actions?.action?.function instanceof Function &&
        props.actions?.action?.display,
);
const hasPreviousInActions = computed(() => props.actions?.action?.previous);

const hasValidButton = computed(() => {
    return (shouldEmitValidation.value || hasFunctionInActions.value) && !props.hideValid;
});
const hasPreviousButton = computed(() => {
    return (
        (shouldEmitPrevious.value || hasPreviousInActions.value) &&
        (props.previousDisplay === undefined || props.previousDisplay === true)
    );
});
const hasAfterButton = computed(() => shouldEmitAfter.value || props.nextDisplay !== false);

const loading = computed(() => {
    return props.loading || localLoading.value || props.actions?.action.loading;
});

const eager = computed(() => {
    return props.eager || false;
});

const dialogTitle = computed((): string => {
    if (props.actions?.action?.title && props.actions?.action?.title instanceof Function) {
        return props.actions?.action.title();
    }
    return props.title || "";
});
const dialogActionTitle = computed((): string => {
    if (props.actions?.action?.title) {
        if (typeof props.actions?.action.title === "function") {
            return props.actions?.action.title();
        }
        return props.actions?.action.title;
    }

    return props.validTitle || "Valider";
});

const dialogActionDisabled = computed(() => {
    if (props.actions?.action?.disabled && props.actions?.action?.disabled instanceof Function) {
        return props.actions?.action.disabled();
    }
    return props.actions?.action?.disabled || false;
});

const dialogDisabled = computed(() => {
    return props.validDisabled || dialogActionDisabled.value;
});

const validEvent = async () => {
    if (props.actions?.action?.function && props.actions?.action?.function instanceof Function) {
        localLoading.value = true;
        try {
            await props.actions?.action.function();
        } catch (e) {
            console.error(e);
        }
        localLoading.value = false;
    }
    emits("valid");
};

const previousEvent = async () => {
    if (props.actions?.action?.previous && props.actions?.action?.previous instanceof Function) {
        localLoading.value = true;
        try {
            await props.actions?.action.previous();
        } catch (e) {
            console.error(e);
        }
        localLoading.value = false;
    }
    emits("previous");
};

const afterEvent = () => {
    emits("after");
};

// ---- SLOT PARAMS ----

const activatorSlotParams = computed(() => {
    return {
        onClick: () => {
            _dialog.value = true;
        },
    };
});

const actionSlotParams = computed(() => {
    return {
        onClick: validEvent,
        disabled: dialogActionDisabled,
    };
});

const closeDialog = () => {
    _dialog.value = false;
    snackbarDialog.value = false;
    emits("close", false);
};

const reduceDialog = () => {
    snackbarDialog.value = true;
};

const mountDialog = () => {
    snackbarDialog.value = false;
};

const afterTransition = () => {
    emits("afterEnter");
};
</script>

<style scoped lang="scss">
.oss-dialog-close {
    display: grid;
    background: #fff;
    border-radius: 50px !important;
    transition-duration: 0.3s;
    height: 56px;
}

.oss-dialog-close:hover {
    height: 112px;
}
</style>
